/** Geburtsdatum als Tag, Monat und Jahr – unabhängig vom Kalender-Widget. */

export const BIRTH_YEAR_MIN = 1900;

export type BirthDateField = "day" | "month" | "year";

export type BirthDateParts = {
  day: string;
  month: string;
  year: string;
};

export type BirthDateIssue = "invalid" | "future" | "tooOld" | "incomplete" | "yearLength";

export type ResolvedBirthDate = {
  parts: BirthDateParts;
  iso: string;
  issue: BirthDateIssue | null;
};

const FIELD_ORDER: BirthDateField[] = ["day", "month", "year"];

export function emptyBirthDateParts(): BirthDateParts {
  return { day: "", month: "", year: "" };
}

export function partsFromIso(iso: string): BirthDateParts {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  const year = match?.[1];
  const month = match?.[2];
  const day = match?.[3];
  if (!year || !month || !day) return emptyBirthDateParts();
  return {
    day: String(Number(day)),
    month,
    year,
  };
}

function clampDigits(value: string, maxLength: number): string {
  return value.replace(/\D/g, "").slice(0, maxLength);
}

/** Vollständiges Datum aus Einfügen oder aus einer Zeichenkette wie im Ausweis. */
export function interpretBirthDateRaw(raw: string): BirthDateParts | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (iso?.[1] && iso[2] && iso[3]) {
    return {
      year: iso[1],
      month: clampDigits(iso[2], 2),
      day: clampDigits(iso[3], 2),
    };
  }

  const spaced = /^(\d{1,2})\s+(\d{1,2})\s+(\d{2}|\d{4})$/.exec(trimmed);
  if (spaced?.[1] && spaced[2] && spaced[3]) {
    return {
      day: clampDigits(spaced[1], 2),
      month: clampDigits(spaced[2], 2),
      year: clampDigits(spaced[3], 4),
    };
  }

  const dmy = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/.exec(trimmed);
  if (dmy?.[1] && dmy[2] && dmy[3]) {
    return {
      day: clampDigits(dmy[1], 2),
      month: clampDigits(dmy[2], 2),
      year: clampDigits(dmy[3], 4),
    };
  }

  const compact = /^(\d{2})(\d{2})(\d{4})$/.exec(trimmed);
  if (compact?.[1] && compact[2] && compact[3]) {
    return {
      day: compact[1],
      month: compact[2],
      year: compact[3],
    };
  }

  const compactShort = /^(\d{2})(\d{2})(\d{2})$/.exec(trimmed);
  if (compactShort?.[1] && compactShort[2] && compactShort[3]) {
    return {
      day: compactShort[1],
      month: compactShort[2],
      year: compactShort[3],
    };
  }

  return null;
}

function fieldMaxLength(field: BirthDateField): number {
  return field === "year" ? 4 : 2;
}

/** „15.03.1948“ oder „3.1948“ auf die Felder verteilen, auch wenn mitten im Tippen. */
export function distributeBirthDateTyping(
  field: BirthDateField,
  raw: string,
  current: BirthDateParts,
): BirthDateParts | null {
  if (!/[./\-\s]/.test(raw)) return null;
  const chunks = raw
    .trim()
    .split(/[./\-\s]+/)
    .map((chunk) => chunk.replace(/\D/g, ""))
    .filter((chunk) => chunk.length > 0);
  if (chunks.length === 0) {
    return { ...current, [field]: "" };
  }

  const startIndex = chunks.length >= 3 ? 0 : FIELD_ORDER.indexOf(field);
  const parts: BirthDateParts = { ...current };
  for (let index = 0; index < chunks.length; index += 1) {
    const target = FIELD_ORDER[startIndex + index];
    const chunk = chunks[index];
    if (!target || chunk === undefined) break;
    parts[target] = chunk.slice(0, fieldMaxLength(target));
  }
  return parts;
}

export function isDaySettled(day: string, finalize: boolean): boolean {
  if (!/^\d+$/.test(day)) return false;
  if (finalize || day.length >= 2) return true;
  return Number(day) >= 4;
}

export function isMonthSettled(month: string, finalize: boolean): boolean {
  if (!/^\d+$/.test(month)) return false;
  if (finalize || month.length >= 2) return true;
  return Number(month) >= 2;
}

/**
 * Monat im Format MM.
 * 2–9 werden sofort zu 02–09. 0 und 1 bleiben einstellig, bis die zweite Ziffer kommt.
 * Beim Verlassen wird 1 zu 01.
 */
export function displayMonth(month: string, finalize = false): string {
  if (!/^\d{1,2}$/.test(month)) return month;
  if (month.length === 2) return month;
  const n = Number(month);
  if (n >= 2 || (finalize && n === 1)) return `0${n}`;
  return month;
}

function isYearSettled(year: string): boolean {
  return /^\d{4}$/.test(year);
}

function shouldAdvance(field: BirthDateField, value: string): boolean {
  if (field === "day") return isDaySettled(value, false);
  // Monat erst nach zwei Stellen (MM) verlassen, damit 05 nicht nach der 0 springt.
  if (field === "month") return /^\d{2}$/.test(value);
  return false;
}

function nextField(field: BirthDateField): BirthDateField | null {
  if (field === "day") return "month";
  if (field === "month") return "year";
  return null;
}

export function focusForParts(parts: BirthDateParts): BirthDateField {
  if (!isDaySettled(parts.day, false)) return "day";
  if (!isMonthSettled(parts.month, false)) return "month";
  return "year";
}

function isCompleteEntry(parts: BirthDateParts): boolean {
  return Boolean(
    parts.day &&
      parts.month &&
      (parts.year.length === 2 || parts.year.length === 4),
  );
}

function withDisplayMonth(parts: BirthDateParts, finalize: boolean): BirthDateParts {
  const month = displayMonth(parts.month, finalize);
  if (month === parts.month) return parts;
  return { ...parts, month };
}

export function readBirthDateEntry(
  field: BirthDateField,
  raw: string,
  current: BirthDateParts,
): { parts: BirthDateParts; focus: BirthDateField | null; finalize: boolean } {
  const trimmed = raw.trim();
  const full = interpretBirthDateRaw(trimmed);
  if (full) {
    const parts = withDisplayMonth(full, true);
    return { parts, focus: focusForParts(parts), finalize: true };
  }

  const distributed = distributeBirthDateTyping(field, raw, current);
  if (distributed) {
    const finalize = isCompleteEntry(distributed);
    const parts = withDisplayMonth(distributed, finalize);
    return {
      parts,
      focus: focusForParts(parts),
      finalize,
    };
  }

  const digits = clampDigits(raw, fieldMaxLength(field));
  const value = field === "month" ? displayMonth(digits, false) : digits;
  const parts = { ...current, [field]: value };
  const following = nextField(field);
  const advance =
    following !== null && shouldAdvance(field, value) && current[following] === "";
  return { parts, focus: advance ? following : null, finalize: false };
}

/** 00–aktuelles Jahr → 2000er, sonst 1900er. Das Feld zeigt danach die vier Stellen. */
export function expandTwoDigitBirthYear(twoDigit: number, today: Date): number {
  const currentTwo = today.getFullYear() % 100;
  return (twoDigit <= currentTwo ? 2000 : 1900) + twoDigit;
}

function calendarDate(year: number, month: number, day: number): Date | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

export function resolveBirthDate(
  parts: BirthDateParts,
  options?: { today?: Date; finalize?: boolean },
): ResolvedBirthDate {
  const today = options?.today ?? new Date();
  const finalize = options?.finalize ?? false;
  let day = parts.day.trim();
  let month = displayMonth(parts.month.trim(), finalize);
  let year = parts.year.trim();

  if (finalize && /^\d{2}$/.test(year)) {
    year = String(expandTwoDigitBirthYear(Number(year), today));
  }

  const nextParts: BirthDateParts = { day, month, year };
  if (!day && !month && !year) {
    return { parts: nextParts, iso: "", issue: null };
  }

  const dayReady = isDaySettled(day, finalize);
  const monthReady = isMonthSettled(month, finalize);
  const yearReady = isYearSettled(year);

  if (!dayReady || !monthReady || !yearReady) {
    if (day.length >= 2 && (Number(day) < 1 || Number(day) > 31)) {
      return { parts: nextParts, iso: "", issue: "invalid" };
    }
    if (month.length >= 2 && (Number(month) < 1 || Number(month) > 12)) {
      return { parts: nextParts, iso: "", issue: "invalid" };
    }
    if (!finalize) {
      return { parts: nextParts, iso: "", issue: null };
    }
    if (year && !yearReady) {
      return { parts: nextParts, iso: "", issue: "yearLength" };
    }
    return { parts: nextParts, iso: "", issue: "incomplete" };
  }

  const dayNum = Number(day);
  const monthNum = Number(month);
  const yearNum = Number(year);
  const date = calendarDate(yearNum, monthNum, dayNum);
  if (!date) {
    return { parts: nextParts, iso: "", issue: "invalid" };
  }
  if (yearNum < BIRTH_YEAR_MIN) {
    return { parts: nextParts, iso: "", issue: "tooOld" };
  }
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (date > todayStart) {
    return { parts: nextParts, iso: "", issue: "future" };
  }

  const iso = [
    String(yearNum).padStart(4, "0"),
    String(monthNum).padStart(2, "0"),
    String(dayNum).padStart(2, "0"),
  ].join("-");
  return { parts: nextParts, iso, issue: null };
}

export function formatBirthDateLong(iso: string, locale: "de" | "en"): string | null {
  const parts = partsFromIso(iso);
  if (!parts.year || !parts.month || !parts.day) return null;
  const date = calendarDate(Number(parts.year), Number(parts.month), Number(parts.day));
  if (!date) return null;
  return new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}
